/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { every, get, isEmpty, isEqual, isNil, pick } from 'lodash';

import AssetDropzone from '../../../common/components/AssetDropzone';
import {
  findAction,
  getAttribute,
  getEndpointByType,
  isFieldDisabled,
  isTranslatable
} from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { getErrorMessage } from '@broadleaf/admin-components/dist/common/utils/requestError';
import { buildHint } from '@broadleaf/admin-components/dist/common/components/Hint';
import { buildHelpText } from '@broadleaf/admin-components/dist/common/components/HelpText';
import IconButton from '@broadleaf/admin-components/dist/common/components/IconButton';
import ChangeHighlight from '@broadleaf/admin-components/dist/tracking/components/ChangeHighlight';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import useRefreshEffect from '@broadleaf/admin-components/dist/common/hooks/useRefreshEffect';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import {
  ContextParameters,
  getInheritedContextState
} from '@broadleaf/admin-components/dist/tracking/utils/ContextRequest';
import useTranslateMode from '@broadleaf/admin-components/dist/form/hooks/useTranslateMode';
import useTracking from '@broadleaf/admin-components/dist/tracking/hooks/useTracking';
import useExternalChangeContainer from '@broadleaf/admin-components/dist/tracking/hooks/useExternalChangeContainer';
import useEnhanceExternalDeleteWithRevert from '@broadleaf/admin-components/dist/tracking/hooks/useEnhanceExternalDeleteWithRevert';
import ItemBrowserAssignModal from '@broadleaf/admin-components/dist/form/item-browser/components/ItemBrowserModals/components/ItemBrowserAssignModal';
import ItemBrowserDeleteModal from '@broadleaf/admin-components/dist/form/item-browser/components/ItemBrowserModals/components/ItemBrowserDeleteModal';
import useItemBrowser from '@broadleaf/admin-components/dist/form/item-browser/hooks/useItemBrowser';
import { openModal } from '@broadleaf/admin-components/dist/form/item-browser/actions/itemBrowser';
import ItemBrowserProvider from '@broadleaf/admin-components/dist/form/item-browser/providers/ItemBrowserProvider';
import { IFormik } from '@broadleaf/admin-components/dist/types/form';
import type {
  IAssetDropzoneMetadata,
  IMetadataFieldComponent
} from '@broadleaf/admin-components/dist/types/metadata';

import messages from './ExternalAssetDropzone.messages';

/**
 * The component that handles uploading an Asset and viewing a preview of it.
 *
 * @visibleName ASSET_DROPZONE
 */
export const ExternalAssetDropzone: React.FC<ExternalAssetDropzoneProps> = props => {
  const translateMode = useTranslateMode();
  const tracking = useTracking();
  const [assetFile, setAssetFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const formatMessage = useFormatMessage();

  const {
    metadata,
    metadata: { label, required, hint, helpText },
    formik
  } = props;

  const {
    containerName,
    pollChangeSummaries,
    sandboxDiscriminated,
    subContainer,
    subContainerSummaries = []
  } = useExternalChangeContainer(props);

  useEffect(
    () => {
      fetchAsset();
    },
    [] // eslint-disable-line
  );

  useRefreshEffect(
    fetchAsset,
    { sandboxDiscriminated, subContainerSummaries },
    didSummaryOperationFinish,
    false
  );

  useRefreshEffect(pollChanges, { assetFile }, undefined, false);

  function pollChanges() {
    if (!!containerName && sandboxDiscriminated) {
      pollChangeSummaries();
    }
  }

  const readEndpoint = useMemo(() => getEndpointByType(metadata, 'READ'), [
    metadata
  ]);
  const uploadEndpoint = useMemo(() => getEndpointByType(metadata, 'UPLOAD'), [
    metadata
  ]);
  const postUploadEndpoint = useMemo(
    () => getEndpointByType(metadata, 'POST_UPLOAD'),
    [metadata]
  );

  const parentIdField = getAttribute(metadata, 'parentIdField', 'id');

  const parent = pick(formik.values, [
    ...getAttribute(metadata, 'dependentFields', []),
    parentIdField
  ]);
  const fetchContextParams = {
    tracking,
    parent
  };

  useRefreshEffect(fetchAsset, fetchContextParams, undefined, false);

  function fetchAsset() {
    if (!readEndpoint) {
      return;
    }

    setError(null);
    setIsLoading(true);

    return request(
      {
        method: 'get',
        ...readEndpoint,
        scope: readEndpoint.scope || metadata.scope
      },
      fetchContextParams
    )
      .then(response => {
        setAssetFile(response.data);
      })
      .catch(errorResponse => {
        // ignore error if there is no image
        // TODO probably better to return nothing on the backend instead of error
        if (get(errorResponse, 'response.status') !== 404) {
          setError(getErrorMessage(errorResponse));
        }
      })
      .then(() => {
        setIsLoading(false);
      });
  }

  const handleUploadAsset = (file, { setProgress }) => {
    setError(null);

    const data = new FormData();
    data.append('file', file);

    const contextParams = {
      [ContextParameters.CONTEXT_STATE]: getInheritedContextState(
        formik.values
      ),
      [ContextParameters.TRACKING]: tracking,
      parent: formik.values
    };

    if (sandboxDiscriminated) {
      contextParams[ContextParameters.CHANGE_CONTAINER] = subContainer;
    }

    return request(
      {
        method: 'post',
        ...uploadEndpoint,
        scope: uploadEndpoint.scope || metadata.scope,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        data,
        onUploadProgress: e => {
          const percentCompleted = Math.round((e.loaded * 100) / e.total);

          if (percentCompleted >= 100) {
            setProgress(100);
          } else {
            setProgress(percentCompleted);
          }
        }
      },
      contextParams
    )
      .then(response => response.data)
      .then(upload =>
        postUploadEndpoint ? postUpload(upload, contextParams) : upload
      )
      .then(asset => {
        setAssetFile(asset);
      })
      .catch(errorResponse => {
        setError(getErrorMessage(errorResponse));
      });
  };

  function postUpload(asset, contextParams) {
    return request(
      {
        method: 'post',
        ...postUploadEndpoint,
        scope: postUploadEndpoint.scope || metadata.scope,
        data: asset
      },
      {
        upload: asset,
        ...contextParams
      }
    ).then(response => response.data);
  }

  function deleteAssets() {
    setError(null);
    setAssetFile(null);
  }

  const accept = getAttribute(metadata, 'accept', '');
  const maxWidth = getAttribute(metadata, 'maxWidth', '100%');
  const maxHeight = getAttribute(metadata, 'maxHeight', '100%');

  function handleRejectFile(rejectedFile) {
    const message = formatMessage(messages.rejectedFile, {
      rejectedFileType: rejectedFile.type,
      accept: `${accept}`
    });

    setError(message);
  }

  const disabled = isFieldDisabled(props, translateMode);

  const dropZoneProps = {
    disabled,
    accept,
    emptyMessage: getAttribute(metadata, 'emptyMessage'),
    srcKey: getAttribute(metadata, 'srcKey', 'url'),
    typeKey: getAttribute(metadata, 'typeKey', 'type'),
    assetStyle: { maxHeight, height: maxHeight, width: maxWidth, maxWidth },
    handleUpload: handleUploadAsset,
    onRejectFile: handleRejectFile
  };

  return (
    <div className="ExternalAssetDropzone tw-mb-5">
      <ChangeHighlight
        changeSummaries={isEmpty(assetFile) ? [] : subContainerSummaries}
        entity={assetFile}
        fullWidth={maxWidth === '100%'}
        style={maxWidth === '100%' ? undefined : { maxWidth }}
      >
        <div
          className={cx(
            'ExternalAssetDropzone tw-relative tw-overflow-hidden',
            props.className,
            {
              disabled: disabled
            }
          )}
        >
          {isLoading && (
            <div
              className="loading-status tw-absolute tw-z-[1] tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center"
              role="status"
            >
              <i className="fa fa-spinner fa-pulse fa-lg tw-text-3xl" />
            </div>
          )}

          <div className="control-label tw-mb-2 tw-mt-1 tw-flex tw-items-center tw-font-normal">
            {required ? <span className="required">{label}</span> : label}
            {!!helpText && buildHelpText(helpText)}
          </div>

          {error && (
            <span className="small text-danger bold tw-my-2">
              <i className="fa fa-exclamation-triangle tw-mr-1" />
              {error}
            </span>
          )}

          <div className="ExternalAssetDropzone__buttonGroup tw-absolute tw-right-0 tw-top-0 rtl:tw-left-0 rtl:tw-right-auto">
            <ExternalPickExistingAssetAction
              {...props}
              disabled={disabled}
              onPickExistingAssetSuccess={setAssetFile}
            />
            <DeleteAssetAction
              asset={assetFile}
              metadata={metadata}
              formik={formik}
              disabled={disabled}
              onDeleteAssetSuccess={deleteAssets}
            />
          </div>

          <AssetDropzone
            {...dropZoneProps}
            className="tw-min-h-[240px] tw-min-w-[240px]"
            value={assetFile}
          />
          {!!hint && buildHint(hint)}
        </div>
      </ChangeHighlight>
    </div>
  );
};

export interface ExternalAssetDropzoneProps {
  /** Class name for the outer wrapper div. */
  className?: string;
  /** Whether or not this component is currently disabled. */
  disabled?: boolean;
  /** Reference to Formik instance **/
  formik: IFormik;
  /** the metadata for this component */
  metadata: IMetadataFieldComponent & IAssetDropzoneMetadata;
}

ExternalAssetDropzone.defaultProps = {
  disabled: false
};

const ExternalPickExistingAssetAction: React.FC<ExternalPickExistingAssetActionProps> = ({
  metadata,
  formik,
  onPickExistingAssetSuccess,
  disabled
}) => {
  const translateMode = useTranslateMode();
  const tracking = useTracking();

  const assignEndpoint = useMemo(() => getEndpointByType(metadata, 'ASSIGN'), [
    metadata
  ]);
  const assignActionDefinition = useMemo(
    () => findAction(metadata, { type: 'ASSIGN' }),
    [metadata]
  );

  const enablePickExistingAsset =
    !isNil(assignEndpoint) && !isNil(assignActionDefinition);

  const { subContainer } = useExternalChangeContainer({ metadata });

  const idKey = getAttribute(metadata, 'idKey', 'id');

  const itemBrowser = useItemBrowser({
    assignConfig: assignEndpoint,
    assignContextParams: {
      [ContextParameters.CONTEXT_STATE]: formik.values.contextState,
      [ContextParameters.LOCALE]: isTranslatable(metadata)
        ? translateMode.locale
        : undefined,
      [ContextParameters.TRACKING]: tracking,
      [ContextParameters.CHANGE_CONTAINER]: subContainer,
      parent: formik.values
    },
    onAssignSuccess: item => {
      onPickExistingAssetSuccess(item);
      return item;
    },
    idKey
  });

  function openItemBrowserModal(e) {
    e.preventDefault();
    e.stopPropagation();

    itemBrowser.dispatch(
      openModal('ASSIGN', {
        actionDefinition: assignActionDefinition
      })
    );
  }

  const modalState = itemBrowser.state.modals.ASSIGN;

  return (
    <ItemBrowserProvider
      dispatch={itemBrowser.dispatch}
      state={itemBrowser.state}
    >
      {enablePickExistingAsset && (
        <IconButton
          className={'ExternalAssetDropzone__button--pick--existing--asset'}
          disabled={disabled}
          label={assignActionDefinition.label}
          name="images"
          onClick={openItemBrowserModal}
        />
      )}
      {!isNil(modalState) && (
        <ItemBrowserAssignModal
          handleAssign={itemBrowser.handleAssign}
          {...modalState.modalProps}
        />
      )}
    </ItemBrowserProvider>
  );
};

interface ExternalPickExistingAssetActionProps {
  formik: IFormik & {
    values?: {
      contextState?: object;
    };
  };
  /** the metadata for this component */
  metadata?: IMetadataFieldComponent & {
    /** additional attributes used by this component */
    attributes?: {
      /** The accepted mimeTypes for files that this component supports. */
      accept?: string | Array<string>;
      /** The message to render when the dropzone is empty. */
      emptyMessage?: string;
      /** The max width of the dropzone in this component.*/
      maxWidth?: number | string;
      /** The max height of the dropzone in this component.*/
      maxHeight?: number | string;
      /** The property key that represents the asset's url, defaults to "contentUrl". */
      srcKey?: string;
      /** The property key that represents the asset's type, defaults to "type". */
      typeKey?: string;
    };
  };
  onPickExistingAssetSuccess?: Function;
  disabled?: boolean;
}

const DeleteAssetAction = ({
  asset,
  metadata,
  formik,
  onDeleteAssetSuccess,
  disabled
}) => {
  const tracking = useTracking();

  const deleteEndpoint = useMemo(() => getEndpointByType(metadata, 'DELETE'), [
    metadata
  ]);
  const deleteActionDefinition = useMemo(
    () => findAction(metadata, { type: 'DELETE' }),
    [metadata]
  );

  const enableDelete = !isNil(deleteEndpoint) && !isNil(deleteActionDefinition);

  const { sandboxDiscriminated, subContainer } = useExternalChangeContainer({
    metadata
  });

  const idKey = getAttribute(metadata, 'idKey', 'id');

  const deleteContextParams = {
    [ContextParameters.TRACKING]: tracking,
    parent: formik.values
  };

  if (sandboxDiscriminated) {
    deleteContextParams[ContextParameters.CHANGE_CONTAINER] = subContainer;
  }

  const itemBrowser = useItemBrowser({
    deleteConfig: {
      ...deleteEndpoint,
      scope:
        isNil(deleteEndpoint) || isNil(deleteEndpoint.scope)
          ? metadata.scope
          : deleteEndpoint.scope
    },
    deleteContextParams,
    onDeleteSuccess: () => {
      onDeleteAssetSuccess();
    },
    idKey
  });

  function openDeleteModal(e) {
    e.preventDefault();
    e.stopPropagation();

    itemBrowser.dispatch(
      openModal('DELETE', {
        actionDefinition: deleteActionDefinition,
        item: asset
      })
    );
  }

  const modalState = itemBrowser.state.modals.DELETE;

  const handleDeleteOrRevert = useEnhanceExternalDeleteWithRevert({
    metadata,
    handleDelete: itemBrowser.handleDelete
  });

  const [handlers] = useState({ handleDelete: handleDeleteOrRevert });

  return (
    <ItemBrowserProvider
      dispatch={itemBrowser.dispatch}
      state={itemBrowser.state}
      handlers={handlers}
    >
      {enableDelete && (
        <IconButton
          className={'ExternalAssetDropzone__button--delete'}
          disabled={isEmpty(asset) || disabled}
          label={deleteActionDefinition.label}
          name="trash"
          onClick={openDeleteModal}
        />
      )}
      {!isNil(modalState) && (
        <ItemBrowserDeleteModal {...modalState.modalProps} />
      )}
    </ItemBrowserProvider>
  );
};

const didSummaryOperationFinish = (nextProps, prevProps) => {
  if (!nextProps.sandboxDiscriminated) return false;

  if (
    isEqual(nextProps.subContainerSummaries, prevProps.subContainerSummaries) ||
    isEmpty(prevProps.subContainerSummaries)
  ) {
    return false;
  }

  return every(nextProps.subContainerSummaries, isRestingSummary);
};

const isActiveSummary = summary => {
  return summary.status.endsWith('ING');
};

const isRestingSummary = summary => {
  return !isActiveSummary(summary);
};

export default ExternalAssetDropzone;
